import { getActiveFaqItemsWithCache, loadCachedFaq } from '../services/landingFaqService';
import { getActiveIntroSectionsWithCache, loadCachedIntro } from '../services/landingIntroService';
import { getSectionsWithCache, loadCachedSections } from '../services/landingPageService';
import type { LandingFaqItem } from '../types/landingFaq';
import type { LandingIntroSection } from '../types/landingIntro';
import type { LandingPageSection } from '../types/landingPage';
import {
  isLandingPublishedMode,
  loadCachedPublishedSnapshot,
  loadLandingPublishedSnapshot,
} from './landingPublishedSnapshot';

export function peekCachedPublicLandingSections(): LandingPageSection[] | null {
  if (isLandingPublishedMode()) {
    const published = loadCachedPublishedSnapshot();
    return published?.sections?.length ? published.sections : null;
  }
  const cached = loadCachedSections();
  if (!cached?.data?.length) return null;
  return cached.data.filter((s) => s.is_active).sort((a, b) => a.section_order - b.section_order);
}

export function peekCachedPublicLandingIntro(): LandingIntroSection[] | null {
  if (isLandingPublishedMode()) {
    const published = loadCachedPublishedSnapshot();
    return published?.intro?.length ? published.intro : null;
  }
  const cached = loadCachedIntro();
  if (!cached?.data?.length) return null;
  return cached.data.filter((x) => x.is_active);
}

export function peekCachedPublicLandingFaq(): LandingFaqItem[] | null {
  if (isLandingPublishedMode()) {
    const published = loadCachedPublishedSnapshot();
    return published?.faq?.length ? published.faq : null;
  }
  const cached = loadCachedFaq();
  if (!cached?.data?.length) return null;
  return cached.data.filter((x) => x.is_active).sort((a, b) => a.sort_order - b.sort_order);
}

export async function loadPublicLandingSections(): Promise<LandingPageSection[]> {
  if (isLandingPublishedMode()) {
    const published = await loadLandingPublishedSnapshot();
    if (!published) {
      throw new Error('발행된 홈 콘텐츠가 없습니다. 배포 시 랜딩 발행이 필요합니다.');
    }
    return published.sections;
  }
  const { sections } = await getSectionsWithCache();
  return sections;
}

export async function loadPublicLandingIntro(): Promise<LandingIntroSection[]> {
  if (isLandingPublishedMode()) {
    const published = await loadLandingPublishedSnapshot();
    if (!published) {
      throw new Error('발행된 서비스 소개가 없습니다. 배포 시 랜딩 발행이 필요합니다.');
    }
    return published.intro;
  }
  const { sections } = await getActiveIntroSectionsWithCache();
  return sections;
}

export async function loadPublicLandingFaq(): Promise<LandingFaqItem[]> {
  if (isLandingPublishedMode()) {
    const published = await loadLandingPublishedSnapshot();
    if (!published) {
      throw new Error('발행된 Q&A가 없습니다. 배포 시 랜딩 발행이 필요합니다.');
    }
    return published.faq;
  }
  const { items } = await getActiveFaqItemsWithCache();
  return items;
}
